/**
 * 運営ユーザー `/staff`。
 *
 * ops は 1 ユーザ 1 ロール（02-control-api §4.1）。複数ロールの和集合を作らせない。
 *
 * 下部の権限マトリクスは **サーバーから取得**する。UI 側に ROLE_PERMISSIONS を
 * 書き写すと、権限を変えたときに画面だけ古いままになり、
 * 「画面ではできると書いてあるのに 403」という最悪の説明を運営者にさせることになる。
 */
import * as React from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Check, MoreHorizontal, ShieldAlert, UserPlus, X } from 'lucide-react';
import {
  Alert,
  AlertDescription,
  Badge,
  Button,
  DataTable,
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
  EmptyState,
  Input,
  PageHeader,
  Section,
  StatusBadge,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
  formatDateTime,
  formatRelative,
  type DataTableColumn,
} from '@mail-sv/ui';
import { STAFF_ROLE, labelOf } from '@mail-sv/shared';
import { api } from '../lib/api.js';
import { qk } from '../lib/query-keys.js';
import { OPS_PERMISSION } from '../lib/permissions.js';
import { useAuth, usePermission } from '../stores/auth.js';
import { FormDialog, SecretRevealDialog } from '../components/dialogs.js';
import { Field, NativeSelect } from '../components/form-fields.js';
import { QueryError, RefetchingDot } from '../components/states.js';
import type { CountedList, StaffCreateResult, StaffUser } from '../types/api.js';

interface PermissionMatrix {
  permissions: string[];
  roles: Array<{ role: string; permissions: string[] }>;
}

export function StaffPage(): React.ReactElement {
  const { user } = useAuth();
  const canWrite = usePermission(OPS_PERMISSION.staffWrite);
  const [inviteOpen, setInviteOpen] = React.useState(false);
  // 作成直後だけ手元に持つ初期パスワード（サーバーは平文を返さない）。
  const [invite, setInvite] = React.useState<{ email: string; password: string } | null>(null);

  const query = useQuery({
    queryKey: qk.staff(),
    queryFn: () => api.get<CountedList<StaffUser>>('/staff'),
  });

  const columns: Array<DataTableColumn<StaffUser>> = [
    {
      id: 'name',
      header: '氏名',
      cell: (s) => (
        <div className="min-w-0">
          <span className="block truncate font-medium">{s.displayName}</span>
          <span className="block truncate text-xs text-muted-foreground">{s.email}</span>
        </div>
      ),
    },
    { id: 'role', header: 'ロール', cell: (s) => <Badge variant="secondary" size="sm">{labelOf(s.role)}</Badge> },
    {
      id: 'mfa',
      header: '2FA',
      align: 'center',
      cell: (s) =>
        s.mfaEnabled ? (
          <Check className="mx-auto size-4 text-success" aria-label="登録済み" />
        ) : (
          <span className="inline-flex items-center gap-1 text-xs text-warning" aria-label="未登録">
            <ShieldAlert className="size-3.5" aria-hidden />
            未登録
          </span>
        ),
    },
    { id: 'status', header: '状態', cell: (s) => <StatusBadge status={s.status === 'disabled' ? 'suspended' : s.status === 'invited' ? 'pending' : 'active'} size="sm" /> },
    { id: 'lastLogin', header: '最終ログイン', hideBelow: 'md', cell: (s) => formatRelative(s.lastLoginAt) },
    { id: 'createdAt', header: '登録日', hideBelow: 'lg', cell: (s) => formatDateTime(s.createdAt) },
    {
      id: 'actions',
      header: <span className="sr-only">操作</span>,
      width: 'w-12',
      align: 'right',
      cell: (s) => <StaffRowActions staff={s} canWrite={canWrite} isSelf={s.id === user?.id} />,
    },
  ];

  const mfaMissing = (query.data?.items ?? []).filter((s) => !s.mfaEnabled && s.status === 'active').length;

  return (
    <>
      <PageHeader
        title="運営ユーザー"
        description="コンソールにログインできるスタッフ。ops は 1 人 1 ロールです。"
        actions={
          <>
            <RefetchingDot active={query.isFetching && !query.isLoading} />
            {canWrite ? (
              <Button onClick={() => setInviteOpen(true)}>
                <UserPlus aria-hidden />
                運営者を追加
              </Button>
            ) : null}
          </>
        }
      />

      {mfaMissing > 0 ? (
        <Alert variant="warning" className="mb-4">
          <AlertDescription>
            2 要素認証が未登録のアクティブなスタッフが {mfaMissing} 名います。ops 面は TOTP 必須のため、
            未登録者は登録画面以外の操作がすべて 403 になります。
          </AlertDescription>
        </Alert>
      ) : null}

      {query.isError ? <QueryError error={query.error} onRetry={() => void query.refetch()} className="mb-4" /> : null}

      <DataTable
        columns={columns}
        rows={query.data?.items ?? []}
        rowKey={(s) => s.id}
        loading={query.isLoading}
        emptyTitle="運営ユーザーが登録されていません"
      />

      <PermissionMatrixSection />

      <InviteDialog open={inviteOpen} onOpenChange={setInviteOpen} onInvited={setInvite} />
      <SecretRevealDialog
        open={invite !== null}
        onOpenChange={() => setInvite(null)}
        title="運営者を追加しました"
        description="初期パスワードはこの画面を閉じると二度と表示できません。安全な経路で本人へ渡してください。初回ログインで変更と TOTP 登録が強制されます。"
        items={
          invite
            ? [
                { label: 'メールアドレス', value: invite.email },
                { label: '初期パスワード', value: invite.password },
              ]
            : []
        }
      />
    </>
  );
}

/* -------------------------------------------------------------- 行アクション */

function StaffRowActions({
  staff,
  canWrite,
  isSelf,
}: {
  staff: StaffUser;
  canWrite: boolean;
  isSelf: boolean;
}): React.ReactElement | null {
  const qc = useQueryClient();
  const [roleOpen, setRoleOpen] = React.useState(false);

  const invalidate = (): void => void qc.invalidateQueries({ queryKey: qk.staff() });

  const resetMfa = useMutation({
    mutationFn: () => api.post(`/staff/${staff.id}/reset-mfa`, {}),
    onSuccess: invalidate,
  });
  const setStatus = useMutation({
    mutationFn: (status: 'active' | 'disabled') => api.patch(`/staff/${staff.id}`, { status }),
    onSuccess: invalidate,
  });
  const revokeSessions = useMutation({
    mutationFn: () => api.post(`/staff/${staff.id}/revoke-sessions`, {}),
    onSuccess: invalidate,
  });

  if (!canWrite) return null;

  return (
    <>
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <Button variant="ghost" size="icon-sm" aria-label={`${staff.displayName} の操作`}>
            <MoreHorizontal aria-hidden />
          </Button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="end" className="w-56">
          <DropdownMenuLabel className="truncate">{staff.displayName}</DropdownMenuLabel>
          <DropdownMenuSeparator />
          {/* 自分自身のロール変更は権限降格の事故になるのでサーバーが 400 を返す。UI でも出さない。 */}
          <DropdownMenuItem onSelect={() => setRoleOpen(true)} disabled={isSelf}>
            ロールを変更
          </DropdownMenuItem>
          <DropdownMenuItem onSelect={() => resetMfa.mutate()} disabled={resetMfa.isPending}>
            2FA をリセット
          </DropdownMenuItem>
          <DropdownMenuItem onSelect={() => revokeSessions.mutate()} disabled={revokeSessions.isPending}>
            セッションを全て失効
          </DropdownMenuItem>
          <DropdownMenuSeparator />
          {staff.status === 'disabled' ? (
            <DropdownMenuItem onSelect={() => setStatus.mutate('active')}>有効化する</DropdownMenuItem>
          ) : (
            <DropdownMenuItem className="text-destructive" onSelect={() => setStatus.mutate('disabled')} disabled={isSelf}>
              <X className="size-4" aria-hidden />
              無効化する
            </DropdownMenuItem>
          )}
        </DropdownMenuContent>
      </DropdownMenu>

      <RoleDialog staff={staff} open={roleOpen} onOpenChange={setRoleOpen} />
    </>
  );
}

function RoleDialog({
  staff,
  open,
  onOpenChange,
}: {
  staff: StaffUser;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}): React.ReactElement {
  const qc = useQueryClient();
  const [role, setRole] = React.useState<string>(staff.role);

  React.useEffect(() => {
    if (open) setRole(staff.role);
  }, [open, staff.role]);

  const mut = useMutation({
    mutationFn: () => api.patch(`/staff/${staff.id}`, { role }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: qk.staff() });
      onOpenChange(false);
    },
  });

  return (
    <FormDialog
      open={open}
      onOpenChange={onOpenChange}
      title={`${staff.displayName} のロールを変更`}
      description="変更は即座に反映され、監査ログに記録されます。"
      submitLabel="変更する"
      pending={mut.isPending}
      error={mut.isError ? mut.error : undefined}
      onSubmit={() => mut.mutate()}
    >
      <Field label="ロール" required>
        <NativeSelect
          value={role}
          onChange={setRole}
          options={STAFF_ROLE.map((r) => ({ value: r, label: labelOf(r) }))}
        />
      </Field>
    </FormDialog>
  );
}

/* -------------------------------------------------------------------- 招待 */

function InviteDialog({
  open,
  onOpenChange,
  onInvited,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onInvited: (result: { email: string; password: string }) => void;
}): React.ReactElement {
  const qc = useQueryClient();
  const [email, setEmail] = React.useState('');
  const [displayName, setDisplayName] = React.useState('');
  const [role, setRole] = React.useState<string>('support');
  const [password, setPassword] = React.useState('');

  React.useEffect(() => {
    if (open) {
      setEmail('');
      setDisplayName('');
      setRole('support');
      // 初期パスワードは**その場で生成する**。運営者に考えさせると短くなるし、
      // サーバーは 16 文字以上しか受け付けない（400 になる）。
      setPassword(generatePassword());
    }
  }, [open]);

  /*
   * **サーバーの契約は `{email, displayName, role, password, allowedIpCidrs?}`。**
   * `password`（16 文字以上）が必須で、以前は送っていなかったため
   * 「運営者を招待」は常に 400 で失敗していた。応答に招待 URL は無い
   * （初回ログインでパスワード変更 + TOTP 登録が強制される方式）。
   */
  const mut = useMutation({
    mutationFn: () =>
      api.post<StaffCreateResult>('/staff', {
        email: email.trim(),
        displayName: displayName.trim() === '' ? email.trim() : displayName.trim(),
        role,
        password,
      }),
    onSuccess: (res) => {
      void qc.invalidateQueries({ queryKey: qk.staff() });
      onOpenChange(false);
      onInvited({ email: res.email, password });
    },
  });

  return (
    <FormDialog
      open={open}
      onOpenChange={onOpenChange}
      title="運営者を追加"
      description="初期パスワードを発行します。初回ログインでパスワード変更と TOTP の登録が強制されます。"
      submitLabel="追加する"
      pending={mut.isPending}
      error={mut.isError ? mut.error : undefined}
      disabled={email.trim().length === 0 || password.length < 16}
      onSubmit={() => mut.mutate()}
    >
      <Field label="メールアドレス" required>
        <Input type="email" value={email} onChange={(e) => setEmail(e.target.value)} autoComplete="off" />
      </Field>
      <Field label="氏名" hint="省略するとメールアドレスをそのまま表示名にします。">
        <Input value={displayName} onChange={(e) => setDisplayName(e.target.value)} autoComplete="off" />
      </Field>
      <Field label="ロール" required hint="最小権限から始めてください。昇格は後からいつでもできます。">
        <NativeSelect value={role} onChange={setRole} options={STAFF_ROLE.map((r) => ({ value: r, label: labelOf(r) }))} />
      </Field>
      <Field
        label="初期パスワード"
        required
        hint="16 文字以上。作成後に一度だけ表示されるので、安全な経路で本人へ渡してください。"
      >
        <div className="flex items-center gap-2">
          <Input
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            autoComplete="off"
            spellCheck={false}
            className="font-mono"
          />
          <Button type="button" variant="outline" size="sm" onClick={() => setPassword(generatePassword())}>
            再生成
          </Button>
        </div>
      </Field>
    </FormDialog>
  );
}

/**
 * 初期パスワードの生成。20 文字・記号なし（口頭やチャットでの誤読を避ける）。
 * サーバーは 16 文字以上しか受け付けないので、下回らない長さで固定する。
 */
export function generatePassword(): string {
  const alphabet = 'abcdefghijkmnopqrstuvwxyzABCDEFGHJKLMNPQRSTUVWXYZ23456789';
  const bytes = new Uint32Array(20);
  crypto.getRandomValues(bytes);
  return Array.from(bytes, (b) => alphabet[b % alphabet.length]).join('');
}

/* ------------------------------------------------------------ 権限マトリクス */

function PermissionMatrixSection(): React.ReactElement {
  const query = useQuery({
    queryKey: ['ops', 'staff', 'permissions'] as const,
    queryFn: () => api.get<PermissionMatrix>('/staff/permissions'),
    staleTime: 10 * 60_000,
  });

  const matrix = query.data;

  return (
    <Section
      title="ロール権限マトリクス"
      description="サーバー側の ROLE_PERMISSIONS をそのまま表示しています（読み取り専用）。"
      className="mt-8"
    >
      {query.isError ? (
        <EmptyState
          title="権限マトリクスを取得できませんでした"
          description="API が /ops/staff/permissions を提供していない可能性があります。"
        />
      ) : null}

      {matrix ? (
        <div className="overflow-x-auto rounded-lg border border-border">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead scope="col" className="min-w-[16rem]">
                  パーミッション
                </TableHead>
                {matrix.roles.map((r) => (
                  <TableHead key={r.role} scope="col" className="text-center">
                    {labelOf(r.role)}
                  </TableHead>
                ))}
              </TableRow>
            </TableHeader>
            <TableBody>
              {matrix.permissions.map((perm) => (
                <TableRow key={perm}>
                  <TableCell>
                    <code className="font-mono text-xs">{perm}</code>
                  </TableCell>
                  {matrix.roles.map((r) => (
                    <TableCell key={r.role} className="text-center">
                      {r.permissions.includes(perm) ? (
                        <Check className="mx-auto size-4 text-success" aria-label="許可" />
                      ) : (
                        <span className="text-muted-foreground" aria-label="不可">
                          —
                        </span>
                      )}
                    </TableCell>
                  ))}
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      ) : null}
    </Section>
  );
}
